/**
 * Datas e horas no relógio do fuso da instalação (IANA), não do navegador de quem vê: a agenda da
 * captura e os horários da SEFAZ são hora local da instalação.
 */
const DIA_MS = 86_400_000;

/** Partes do instante no relógio do fuso da instalação (não do navegador de quem vê). */
function partes(instante: Date, fuso: string) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: fuso,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
      .formatToParts(instante)
      .filter((x) => x.type !== 'literal')
      .map((x) => [x.type, Number(x.value)]),
  ) as Record<string, number | undefined>;
  return {
    dia: Date.UTC(p['year'] ?? 0, (p['month'] ?? 1) - 1, p['day'] ?? 1),
    minutos: (p['hour'] ?? 0) * 60 + (p['minute'] ?? 0),
  };
}

/** "hoje 16:45", "amanhã 08:00", "seg., 20/10 08:00" — no fuso da instalação. */
export function formatarProxima(iso: string, fuso: string, agora: Date = new Date()): string {
  const quando = new Date(iso);
  const dias = Math.round((partes(quando, fuso).dia - partes(agora, fuso).dia) / DIA_MS);
  const hora = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    hour: '2-digit',
    minute: '2-digit',
  }).format(quando);
  if (dias === 0) return `hoje ${hora}`;
  if (dias === 1) return `amanhã ${hora}`;
  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(quando);
  return `${dia} ${hora}`;
}

/** Minuto do dia no relógio da instalação (a linha "agora" da régua). */
export function minutosDeAgora(fuso: string, agora: Date = new Date()): number {
  return partes(agora, fuso).minutos;
}

const HORA = (fuso: string) =>
  new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' });

/** "13:05" no fuso. */
export function formatarHoraNoFuso(iso: string, fuso: string): string {
  return HORA(fuso).format(new Date(iso));
}

/** "15/10 12:00" no fuso (tabelas densas, o ano está no contexto). */
export function formatarDiaHoraNoFuso(iso: string, fuso: string): string {
  const quando = new Date(iso);
  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    day: '2-digit',
    month: '2-digit',
  }).format(quando);
  return `${dia} ${HORA(fuso).format(quando)}`;
}

/** "em 38 min", "em 2 h 5 min", "agora" — distância até um instante futuro. */
export function emQuanto(iso: string, agora: Date = new Date()): string {
  const minutos = Math.ceil((new Date(iso).getTime() - agora.getTime()) / 60_000);
  if (minutos <= 0) return 'agora';
  if (minutos < 60) return `em ${String(minutos)} min`;
  const resto = minutos % 60;
  return `em ${String(Math.floor(minutos / 60))} h${resto ? ` ${String(resto)} min` : ''}`;
}
